import React from 'react';
import Mapir from "mapir-react-component";
import { MapPin, Navigation } from "lucide-react";
import 'mapir-react-component/dist/index.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import { trackEvent } from '../utils/analytics';
import { mapirMap } from '../utils/mapirMap';

// This is the filled map-pin form of the same Lucide MapPin icon used beside
// the address below. Mapir markers accept an image URL rather than a React icon.
const placeLocationMarker = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="44" height="52" viewBox="0 0 24 28">
    <defs>
      <filter id="marker-shadow" x="-40%" y="-30%" width="180%" height="180%">
        <feDropShadow dx="0" dy="2" stdDeviation="1.6" flood-color="#1f2937" flood-opacity=".28"/>
      </filter>
    </defs>
    <path d="M20 10c0 5.5-8 12-8 12S4 15.5 4 10a8 8 0 1 1 16 0Z" fill="#038545" stroke="#ffffff" stroke-width="1.5" filter="url(#marker-shadow)"/>
    <circle cx="12" cy="10" r="2.7" fill="#ffffff"/>
  </svg>
`)}`;

interface MapComponentProps {
  mapUrl?: string;
  latitude?: number;
  longitude?: number;
  name?: string;
  address?: string;
  placeId?: string;
}

const MapComponent: React.FC<MapComponentProps> = ({ mapUrl, latitude, longitude, name, address, placeId }) => {
  const hasCoordinates = latitude != null && longitude != null;
  const directionsUrl = hasCoordinates
    ? `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`
    : undefined;

  return (
    <section className="w-full rounded-xl bg-transparent px-0 py-4 tablet:bg-transparent tablet:px-0 tablet:py-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-[1.09rem] font-myYekanFaNumExtraBold leading-[1.5] text-[#222222]">نقشه</h2>
        {directionsUrl && (
          <a
            href={directionsUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`مسیریابی به ${name || "مکان"}`}
            className="hidden"
          >
            <Navigation size={16} aria-hidden="true" />
            <span>مسیریابی</span>
          </a>
        )}
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">

        {hasCoordinates ? (
          <Mapir
            center={[longitude, latitude]}
            Map={mapirMap}
            zoom={[15]}
            interactive={true}
            hash={false}
            trackResize={true}
            containerStyle={{
              width: '100%',
              height: '200px'
            }}
          >
            <Mapir.Marker
              coordinates={[longitude, latitude]}
              anchor="bottom"
              Image={placeLocationMarker}
              style={{ width: "44px" }}
            />
          </Mapir>
        ) : (
          <div className="py-8 text-center text-[0.77rem] leading-[1.8] text-gray-500 font-myYekanRegular">
            اطلاعات موقعیت مکانی موجود نیست
          </div>
        )}
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-3 py-3 desktop:gap-6 desktop:px-5 desktop:py-4">
          <div className="flex min-w-0 items-start gap-1.5 text-right">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[#038545] desktop:h-6 desktop:w-6" aria-hidden="true" />
            <div className="min-w-0">
              <p className="truncate text-s font-myYekanDemibold text-gray-900">{address || name || "موقعیت مکان"}</p>
              {address && <p className="mt-1 truncate text-xs text-gray-500">{name}</p>}
            </div>
          </div>
          {directionsUrl && (
            <a
              href={directionsUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("click_navigation", {
                place_id: placeId || "unknown",
                place_name: name || "unknown",
                surface: "map_section",
              })}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-1.5 rounded-xl bg-[#11113] px-4 text-xs font-myYekanDemibold text-white transition-colors hover:bg-black focus:outline-none focus-visible:ring-2 focus-visible:ring-[#038545] focus-visible:ring-offset-2 desktop:h-auto desktop:gap-2 desktop:rounded-xl desktop:border desktop:border-[#038545] desktop:bg-[#068646] desktop:p-2 desktop:text-[0.77rem] desktop:font-myYekanMedium desktop:hover:bg-[#038545]"
            >
              <Navigation size={16} aria-hidden="true" />
              <span>مسیریابی</span>
            </a>
          )}
        </div>
      </div>

    </section>
  );
};

export default MapComponent;
